import React, { useRef } from 'react'
import { TinsightParam } from '../../shared/types/computedKeyParam.type';
import { IEffortZoneTrainingType } from '../../shared/types/Stabledata.type';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { isEmpty, min, orderBy, toNumber } from 'lodash';
import { Box, Card, CardContent, Container, Divider, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import palette from '../../theme/palette';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);


const Z3_KEY = 'insight.z3_duration'
const Z4_KEY = 'insight.z4_duration'
const Z5_KEY = 'insight.z5_duration'
const TIME_BEST_600_KEY = 'insight.time_best_600';
const ORDER = [Z5_KEY, Z4_KEY, Z3_KEY]

function EffortZoneTrainingTypePage({ data, insightParams }: { data: IEffortZoneTrainingType; insightParams: TinsightParam[] }) {
    const chartRef = useRef<HighchartsReactRefObject | null>(null);



    const sorted = orderBy(data.training_type_averages, (item) => item?.averages?.find(key => key.parameter_name === TIME_BEST_600_KEY)?.raw_numeric_value || 0, 'asc')



    const getTrainingTypes = () => {
        if (isEmpty(sorted)) return []

        return sorted.map(tt => `${tt.training_type?.display_name || ''}<br><span style="font-size: 12px; color: gray">${tt?.track_surface?.display_name || ''}</span>`)

    }



    const renderData = () => {
        if (isEmpty(sorted)) return []

        return sorted.map(item => {
            // build a lookup from averages
            const map = Object.fromEntries(
                item.averages
                    .filter(avg => avg.parameter_name !== TIME_BEST_600_KEY)
                    .map(avg => [avg.parameter_name, toNumber(avg.raw_numeric_value?.toFixed(2))])
            )



            // return values in the right order
            return ORDER.map(key => map[key] ?? 0)
        })

    }


    const getBest600Data = () => {
        if (isEmpty(sorted)) return []
        return sorted.map(e => toNumber((e.averages.find(avg => avg.parameter_name === TIME_BEST_600_KEY)?.raw_numeric_value || 0).toFixed(2)))
    }



    const getParamName = (key: string) => {
        return (insightParams.find(param => param.name === key)?.name_translated?.concat(` (${t('firstSection:average')})`) || "")
    }

    const categories = getTrainingTypes()
    const dataByGroup = renderData(); // [[part1, part2, part3], [..], ..]

    const colors = ['#000080', '#005A9C', '#00BFFF'];
    const legends = [getParamName(Z5_KEY), getParamName(Z4_KEY), getParamName(Z3_KEY)]
    // Transpose: turn [[a,b,c], [a,b,c], ...] into [[a,a,a], [b,b,b], [c,c,c]]
    const partsCount = dataByGroup[0]?.length || 0;
    const series: Highcharts.SeriesColumnOptions[] = Array.from({ length: partsCount }, (_, partIndex) => ({
        type: 'column',
        data: dataByGroup.map(group => group[partIndex]),
        stack: 'main',
        yAxis: 0,
        color: colors[partIndex % colors.length],    // cycle colors if more parts than colors
        name: legends[partIndex % legends.length] // cycle colors if more parts than colors

    }));

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            height: 550,
            backgroundColor: 'transparent'

        },
        title: {
            text: ''
        },
        credits: {
            enabled: false,
        },
        xAxis: {
            categories
        },
        tooltip: {
            enabled: false
        },
        legend: {
            symbolPadding: 5,  // space between symbol and text
            itemDistance: 40,   // horizontal space between legend items
            itemMarginTop: 10,  // vertical space between rows (if wrapping to multiple lines)
            itemMarginBottom: 10,
            verticalAlign: 'top',
            align: 'center'
        },
        yAxis: [{
            min: 0,
            startOnTick: true,    // prevent auto-adjust downwards
            endOnTick: true,
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: t('stable:key:avgZoneDuration'),
            },
            labels: {
                formatter: function () {

                    return dayjs
                        .duration(Number(this.value), 'seconds')
                        .format('mm:ss');
                }
            }
        }, {
            min: min(getBest600Data() || 0),
            startOnTick: true,    // prevent auto-adjust downwards
            endOnTick: true,
            gridLineWidth: 0,// disables horizontal grid lines
            title: {
                text: getParamName(TIME_BEST_600_KEY),
            },
            labels: {
                formatter: function () {
                    // Only use 2 decimals if the range is small; otherwise keep it clean
                    if (toNumber(this.value) % 1 === 0) {
                        return this.value.toString();
                    }
                    return Highcharts.numberFormat(toNumber(this.value), 2);
                },
            },
            opposite: true,
        },],
        plotOptions: {
            series: {
                states: {
                    hover: {
                        enabled: false
                    },
                    inactive: {
                        opacity: 1
                    }
                }
            },
            column: {
                stacking: 'normal', // could be 'percent'
                dataLabels: {
                    enabled: true,
                    formatter: function () {

                        return dayjs
                            .duration(Number(this.y), 'seconds')
                            .format('mm:ss');
                    }

                }
            },
            spline: {
                dataLabels: {
                    allowOverlap: true,
                    enabled: true,
                    useHTML: true,
                    formatter: function () {
                        return `<div style="
                        background: ${palette.light.primary.light};
                        border-radius: 4px;
                        padding: 2px ;
                        color:#fff;
                        font-size: 10px;
                        box-shadow: 0 1px 3px ${palette.light.primary.light};
                        white-space: nowrap;
                    ">${(Number(this.y)).toFixed(2)}</div>`;
                    },
                    align: 'left', // position relative to point
                    verticalAlign: 'bottom',
                    y: 20,
                    x: 10

                },
                marker: {
                    enabled: true,
                    radius: 4
                }
            },

        },

        series: [...series, {
            type: 'spline',
            data: getBest600Data(),
            name: getParamName(TIME_BEST_600_KEY),
            color: palette.light.primary.main,
            yAxis: 1,
        }]
    }



    const getExplicationByKey = (key: string) => {
        switch (key) {
            case 'best_time_best_600':
                return t('stable:key:bestTime600tt')
            case 'worst_time_best_600':
                return t('stable:key:lowBestTime600tt')
            case 'best_z5_duration':
                return t('stable:key:bestz5tt')
            default:
                break;
        }
    }


    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                <Typography variant="h4" sx={{ color: palette.light.primary.main }}>
                    {t('stable:title:'.concat(data.type))}
                </Typography>
                <img
                    style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15 }}
                    src="/arioneo_logo_white.png"
                    alt="arioneo logo"
                />
            </Box>
            <div style={{ width: '1200px' }} id="chart-container">
                <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
            </div>
            {
                !isEmpty(data.records) && <Card>
                    <CardContent>
                        <Grid container spacing={3}>
                            {data.records.map((record, index) => (
                                <>
                                    <Grid item md={4} display={'flex'} flexDirection={'column'}>
                                        <Box  >
                                            <Typography variant='subtitle1' fontSize={'19px'} justifySelf={'flex-start'}>{record.formatted_value}</Typography>
                                        </Box>
                                        <Box >
                                            <Typography variant='body2' fontSize={'15px'} color={'gray'} alignSelf={'start'}>{getExplicationByKey(record.parameter_name)}</Typography>
                                        </Box>
                                    </Grid>
                                    {index !== data.records.length - 1 && <Divider />}
                                </>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            }


        </>
    )
}

export default EffortZoneTrainingTypePage